/**
 * ConversationsRail.jsx — left rail on the Chat screen listing every past
 * conversation with the current user's Rammy. Click a row → parent loads
 * that conversation's messages. Includes rename, delete, share, and a
 * "+ New chat" button. Search over titles + last snippet (client-side).
 *
 * Design goals:
 *   • Feel like the Studio project rail: monochrome, warm, cyan for active
 *   • Zero surprises — row click switches, per-row menu for destructive ops
 *   • Empty state guides the user to just start typing on the right
 */
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as L from "lucide-react";

export default function ConversationsRail({ activeId, onSelect, onNew, refreshKey, hidden = false, onToggleHidden }) {
  const [convs, setConvs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [collapsed, setCollapsed] = useState({}); // { folder_id: true }
  const [loading, setLoading] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false); // header "..." menu
  const [query, setQuery] = useState("");
  const [menuFor, setMenuFor] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [folderDialog, setFolderDialog] = useState(null); // { mode: 'new'|'edit', folder? }
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [convRes, folderRes] = await Promise.all([
        api.get("/conversations"),
        api.get("/folders"),
      ]);
      setConvs(Array.isArray(convRes.data) ? convRes.data : []);
      setFolders(Array.isArray(folderRes.data) ? folderRes.data : []);
    } catch (_) {
      toast.error("Couldn't load conversations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  // Cmd/Ctrl+N — the widely-expected shortcut to start a fresh conversation.
  // We intercept only when the user isn't typing in an input/textarea so we
  // don't hijack "select all" or the composer's Enter behaviour.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onNew?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNew]);

  const filtered = useMemo(() => {
    if (!query.trim()) return convs;
    const q = query.toLowerCase();
    return convs.filter(c =>
      (c.title || "").toLowerCase().includes(q) ||
      (c.last_snippet || "").toLowerCase().includes(q)
    );
  }, [convs, query]);

  // Partition into pinned first, then per-folder groups, then unfoldered.
  const pinnedRows = filtered.filter(c => c.pinned);
  const foldered   = filtered.filter(c => !c.pinned && c.folder_id);
  const unfoldered = filtered.filter(c => !c.pinned && !c.folder_id);
  const rowsByFolder = folders.reduce((acc, f) => {
    acc[f.id] = foldered.filter(c => c.folder_id === f.id);
    return acc;
  }, {});

  const moveToFolder = async (cid, folderId) => {
    try {
      await api.patch(`/conversations/${cid}`, { folder_id: folderId || "__unfolder__" });
      load();
    } catch (_) { toast.error("Couldn't move"); }
    setMenuFor(null);
  };

  const runSuggest = async () => {
    setSuggestLoading(true);
    setSuggestOpen(true);
    try {
      const { data } = await api.post("/folders/suggest");
      setSuggestions((data.groups || []).map(g => ({ ...g, checked: true })));
    } catch (_) { toast.error("Suggest failed"); }
    finally { setSuggestLoading(false); }
  };

  const applySuggest = async () => {
    const groups = suggestions.filter(g => g.checked && g.conversation_ids?.length);
    if (!groups.length) { setSuggestOpen(false); return; }
    try {
      const { data } = await api.post("/folders/suggest/apply", { groups });
      toast.success(`Created ${data.folders_created} folder${data.folders_created === 1 ? '' : 's'}, moved ${data.conversations_moved} chats`);
      setSuggestOpen(false);
      setSuggestions([]);
      load();
    } catch (_) { toast.error("Couldn't apply"); }
  };

  const remove = async (cid) => {
    if (!confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      await api.delete(`/conversations/${cid}`);
      setConvs(prev => prev.filter(c => c.id !== cid));
      if (activeId === cid) onNew?.();
      toast.success("Deleted");
    } catch (_) {
      toast.error("Couldn't delete");
    }
    setMenuFor(null);
  };

  const startRename = (c) => {
    setRenamingId(c.id);
    setRenameDraft(c.title || "");
    setMenuFor(null);
  };

  const togglePin = async (c) => {
    const nextPinned = !c.pinned;
    // Optimistic UI: flip locally, then reload from server so the sort order
    // (pinned first) refreshes without a flicker.
    setConvs(prev => prev.map(row => row.id === c.id ? { ...row, pinned: nextPinned } : row));
    try {
      await api.patch(`/conversations/${c.id}`, { pinned: nextPinned });
      load();
    } catch (_) {
      toast.error("Couldn't update pin");
      setConvs(prev => prev.map(row => row.id === c.id ? { ...row, pinned: !nextPinned } : row));
    }
  };

  const commitRename = async () => {
    const cid = renamingId;
    const title = renameDraft.trim();
    if (!cid || !title) { setRenamingId(null); return; }
    try {
      await api.patch(`/conversations/${cid}`, { title });
      setConvs(prev => prev.map(c => c.id === cid ? { ...c, title } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename");
    } finally {
      setRenamingId(null);
    }
  };

  // Collapsed / slim state — a 48px rail with just the essential actions so
  // the chat surface can breathe. Opens instantly when the user asks for
  // history.
  if (hidden) {
    return (
      <div
        className="w-[52px] shrink-0 border-r border-[var(--rammy-border)] bg-[var(--rm-surface)]/40 flex flex-col items-center py-2 gap-1.5"
        data-testid="conversations-rail-collapsed"
      >
        <button
          type="button"
          onClick={() => onToggleHidden?.(false)}
          className="w-8 h-8 rounded-lg text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition flex items-center justify-center"
          data-testid="conversations-rail-expand"
          aria-label="Show conversations"
          title="Show conversations"
        >
          <L.PanelLeftOpen size={16}/>
        </button>
        <button
          type="button"
          onClick={onNew}
          className="w-8 h-8 rounded-lg border border-[var(--rammy-cyan)]/50 text-[var(--rammy-cyan-dark)] bg-transparent hover:bg-[var(--rammy-cyan-soft)] transition-colors flex items-center justify-center"
          data-testid="conversations-rail-new-collapsed"
          aria-label="New chat"
          title="New chat"
        >
          <L.Plus size={14}/>
        </button>
        <button
          type="button"
          onClick={() => onToggleHidden?.(false)}
          className="w-8 h-8 rounded-lg text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition flex items-center justify-center"
          data-testid="conversations-rail-history"
          aria-label="Browse conversations"
          title="Browse conversations"
        >
          <L.MessagesSquare size={14}/>
        </button>
      </div>
    );
  }

  return (
    <div className="w-[240px] shrink-0 border-r border-[var(--rammy-border)] bg-[var(--rm-surface)]/40 flex flex-col" data-testid="conversations-rail">
      {/* Header — label + collapse + secondary "..." menu. */}
      <div className="h-12 border-b border-[var(--rammy-border)] px-4 flex items-center justify-between shrink-0 gap-2">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Conversations</div>
        <div className="flex items-center gap-1">
          {onToggleHidden && (
            <button
              type="button"
              onClick={() => onToggleHidden(true)}
              className="w-7 h-7 rounded-lg hover:bg-[var(--rammy-surface-alt)] flex items-center justify-center text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition"
              data-testid="conversations-collapse"
              title="Collapse — more room to chat"
              aria-label="Collapse conversations"
            >
              <L.PanelLeftClose size={13}/>
            </button>
          )}
          <div className="relative">
          <button
            onClick={() => setMoreOpen(v => !v)}
            className="w-7 h-7 rounded-lg hover:bg-[var(--rammy-surface-alt)] flex items-center justify-center text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition"
            data-testid="conversations-more"
            title="Organize"
          >
            <L.MoreHorizontal size={14}/>
          </button>
          {moreOpen && (
            <>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="fixed inset-0 z-[35] cursor-default"
                aria-hidden="true"
                tabIndex={-1}
              />
              <div
                className="absolute right-0 mt-1 w-48 rounded-xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] rammy-shadow-lg z-[36] overflow-hidden fade-up"
                data-testid="conversations-more-menu"
              >
                <button
                  onClick={() => { setMoreOpen(false); runSuggest(); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2"
                  data-testid="conversations-suggest"
                >
                  <L.Sparkles size={12} className="text-[var(--rammy-cyan-dark)]"/> Suggest folders
                </button>
                <button
                  onClick={() => { setMoreOpen(false); setFolderDialog({ mode: "new" }); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2"
                  data-testid="conversations-new-folder"
                >
                  <L.FolderPlus size={12} className="text-[var(--rammy-cyan-dark)]"/> New folder
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* PRIMARY action — the "New chat" button. Full-width, high-contrast,
          matches the ChatGPT/Claude "prominent big button at the top of the
          sidebar" pattern so this action is unmistakable. */}
      <div className="px-3 pt-3 pb-1 shrink-0">
        <button
          onClick={onNew}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--rammy-cyan)]/55 text-[var(--rammy-cyan-dark)] bg-transparent px-3 py-2.5 text-sm font-medium hover:bg-[var(--rammy-cyan-soft)] transition-colors"
          data-testid="conversations-new"
          title="Start a new chat (⌘/Ctrl+N)"
        >
          <L.Plus size={16} strokeWidth={2.2}/> New chat
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-[var(--rammy-border)] shrink-0">
        <div className="relative">
          <L.Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]"/>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats…"
            className="w-full rounded-full bg-[var(--rammy-surface-alt)] border border-transparent focus:border-[var(--rammy-cyan)] focus:bg-[var(--rm-surface)] pl-7 pr-3 py-1.5 text-xs outline-none transition"
            data-testid="conversations-search"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto subtle-scroll">
        {loading && (
          <div className="p-4 text-sm text-[var(--rammy-text-muted)]">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-5 text-[12px] text-[var(--rammy-text-sec)] leading-relaxed">
            {query
              ? <>No matches for <em className="not-italic text-[var(--rammy-text)]">{query}</em>.</>
              : <>Nothing here yet — start chatting on the right and every conversation will show up in this rail.</>}
          </div>
        )}

        {pinnedRows.length > 0 && (
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] flex items-center gap-1.5" data-testid="conversations-pinned-header">
            <L.Pin size={10}/> Pinned
          </div>
        )}
        {pinnedRows.map(c => renderRow(c, true))}

        {folders.map(f => {
          const rows = rowsByFolder[f.id] || [];
          if (rows.length === 0 && !query) return renderFolderHeader(f, [], true); // empty folder still visible when not searching
          if (rows.length === 0 && query) return null;
          const isCollapsed = collapsed[f.id];
          return (
            <div key={f.id} data-testid={`conversations-folder-${f.id}`}>
              {renderFolderHeader(f, rows, isCollapsed)}
              {!isCollapsed && rows.map(c => renderRow(c, false))}
            </div>
          );
        })}

        {unfoldered.length > 0 && (folders.length > 0 || pinnedRows.length > 0) && (
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
            Unfoldered
          </div>
        )}
        {unfoldered.map(c => renderRow(c, false))}
      </div>

      {folderDialog && (
        <FolderDialog
          mode={folderDialog.mode}
          folder={folderDialog.folder}
          onClose={() => setFolderDialog(null)}
          onSaved={() => { setFolderDialog(null); load(); }}
        />
      )}
      {suggestOpen && (
        <SuggestDialog
          loading={suggestLoading}
          suggestions={suggestions}
          setSuggestions={setSuggestions}
          onClose={() => setSuggestOpen(false)}
          onApply={applySuggest}
        />
      )}
    </div>
  );

  function renderFolderHeader(f, rows, isCollapsed) {
    return (
      <div
        key={f.id}
        className="group px-4 pt-3 pb-1 flex items-center justify-between hover:bg-[var(--rammy-surface-alt)]/50 cursor-pointer"
        onClick={() => setCollapsed(c => ({ ...c, [f.id]: !isCollapsed }))}
        data-testid={`conversations-folder-header-${f.id}`}
      >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">
          <span className="text-[13px]">{f.emoji || "📁"}</span>
          <span className="truncate">{f.name}</span>
          <span className="text-[var(--rammy-text-muted)] font-mono normal-case">{rows.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setFolderDialog({ mode: "edit", folder: f }); }}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[var(--rm-surface)] text-[var(--rammy-text-muted)]"
            data-testid={`conversations-folder-edit-${f.id}`}
            aria-label="Edit folder"
          >
            <L.Pencil size={10}/>
          </button>
          {isCollapsed ? <L.ChevronRight size={11} className="text-[var(--rammy-text-muted)]"/> : <L.ChevronDown size={11} className="text-[var(--rammy-text-muted)]"/>}
        </div>
      </div>
    );
  }

  function renderRow(c, isPinned) {
    return (
          <div
            key={c.id}
            data-testid={`conversations-row-${c.id}`}
            data-active={activeId === c.id ? "true" : "false"}
            data-pinned={isPinned ? "true" : "false"}
            className={`group relative border-b border-[var(--rammy-border)]/60 transition ${
              activeId === c.id ? "bg-[var(--rammy-cyan)]/15" : "hover:bg-[var(--rammy-surface-alt)]"
            }`}
          >
            {renamingId === c.id ? (
              <input
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setRenamingId(null);
                }}
                className="w-full px-4 py-3 text-sm bg-[var(--rm-surface)] border-none outline-none focus:ring-2 focus:ring-[var(--rammy-cyan)]"
                maxLength={140}
                data-testid={`conversations-rename-input-${c.id}`}
              />
            ) : (
              <button
                onClick={() => onSelect(c.id)}
                className={`w-full text-left px-3.5 py-2.5 border-l-2 transition ${
                  activeId === c.id
                    ? "border-l-[var(--rammy-cyan-dark)]"
                    : "border-l-transparent hover:border-l-[var(--rammy-cyan)]/30"
                }`}
                data-testid={`conversations-open-${c.id}`}
              >
                <div className={`text-[13px] truncate flex items-center gap-1.5 ${
                  activeId === c.id ? "font-semibold text-[var(--rammy-text)]" : "font-medium text-[var(--rammy-text)]"
                }`}>
                  {isPinned && <L.Pin size={10} className="text-[var(--rammy-cyan-dark)] shrink-0" fill="currentColor"/>}
                  <span className="truncate">{c.title || "Untitled"}</span>
                </div>
                <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5 truncate">
                  {(c.last_snippet || "").replace(/\s+/g, " ").trim() || "no messages yet"}
                </div>
                {c.updated_at && (
                  <div className="text-[10px] text-[var(--rammy-text-muted)]/80 mt-0.5">
                    {formatRelative(c.updated_at)}
                  </div>
                )}
              </button>
            )}

            {renamingId !== c.id && (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id); }}
                className={`absolute top-2 right-2 p-1 rounded-full hover:bg-[var(--rm-surface)] text-[var(--rammy-text-muted)] transition ${
                  isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
                data-testid={`conversations-menu-${c.id}`}
                aria-label="Actions"
              >
                <L.MoreHorizontal size={14}/>
              </button>
            )}

            {menuFor === c.id && (
              <div
                className="absolute top-8 right-2 z-10 rounded-xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] shadow-lg py-1 w-48"
                data-testid={`conversations-menu-open-${c.id}`}
                onMouseLeave={() => setMenuFor(null)}
              >
                <button
                  onClick={() => { togglePin(c); setMenuFor(null); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2"
                  data-testid={`conversations-pin-${c.id}`}
                >
                  {c.pinned
                    ? <><L.PinOff size={11}/> Unpin</>
                    : <><L.Pin size={11}/> Pin to top</>}
                </button>
                {folders.length > 0 && (
                  <div className="border-t border-[var(--rammy-border)]/60 my-1"/>
                )}
                {folders.map(f => (
                  <button
                    key={f.id}
                    onClick={() => moveToFolder(c.id, f.id)}
                    disabled={c.folder_id === f.id}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`conversations-move-${c.id}-${f.id}`}
                  >
                    <span className="text-sm">{f.emoji || "📁"}</span>
                    <span className="truncate">{f.name}</span>
                    {c.folder_id === f.id && <L.Check size={10} className="ml-auto text-emerald-500"/>}
                  </button>
                ))}
                {c.folder_id && (
                  <button
                    onClick={() => moveToFolder(c.id, null)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2"
                    data-testid={`conversations-unfolder-${c.id}`}
                  >
                    <L.FolderMinus size={11}/> Remove from folder
                  </button>
                )}
                {folders.length > 0 && (
                  <div className="border-t border-[var(--rammy-border)]/60 my-1"/>
                )}
                <button
                  onClick={() => startRename(c)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--rammy-surface-alt)] flex items-center gap-2"
                  data-testid={`conversations-rename-${c.id}`}
                >
                  <L.Pencil size={11}/> Rename
                </button>
                <button
                  onClick={() => remove(c.id)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-500 flex items-center gap-2"
                  data-testid={`conversations-delete-${c.id}`}
                >
                  <L.Trash2 size={11}/> Delete
                </button>
              </div>
            )}
          </div>
    );
  }
}

function formatRelative(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_) {
    return "";
  }
}

const EMOJI_PALETTE = ["📁","📚","💼","🎨","🍳","💡","🧘","🎯","🎬","🏋️","🧠","💌","🌿","🎵","👨‍👩‍👧","🐾"];

function FolderDialog({ mode, folder, onClose, onSaved }) {
  const [name, setName] = useState(folder?.name || "");
  const [emoji, setEmoji] = useState(folder?.emoji || "📁");
  const [systemPrompt, setSystemPrompt] = useState(folder?.system_prompt || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), emoji, system_prompt: systemPrompt };
      if (mode === "new") await api.post("/folders", body);
      else                await api.patch(`/folders/${folder.id}`, body);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm("Delete this folder? Chats inside will move to Unfoldered.")) return;
    try {
      await api.delete(`/folders/${folder.id}`);
      onSaved?.();
    } catch (_) { toast.error("Couldn't delete"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="folder-dialog">
      <div className="bg-[var(--rm-surface)] rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">
          {mode === "new" ? "New folder" : "Edit folder"}
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="text-3xl">{emoji}</div>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Folder name"
            className="flex-1 text-xl font-display bg-transparent border-b border-[var(--rammy-border)] focus:border-[var(--rammy-cyan)] outline-none py-1"
            maxLength={60}
            data-testid="folder-dialog-name"
            autoFocus
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {EMOJI_PALETTE.map(e => (
            <button
              key={e}
              onClick={() => setEmoji(e)}
              className={`text-xl w-9 h-9 rounded-xl transition ${emoji === e ? "bg-[var(--rammy-cyan)]/25 ring-2 ring-[var(--rammy-cyan)]" : "hover:bg-[var(--rammy-surface-alt)]"}`}
              data-testid={`folder-emoji-${e}`}
            >{e}</button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">
          Workspace instructions (optional)
        </div>
        <textarea
          value={systemPrompt}
          onChange={e => setSystemPrompt(e.target.value)}
          placeholder="e.g. You're helping me write a coming-of-age novel about a bakery in Old Delhi. Keep tone lyrical."
          rows={4}
          maxLength={2000}
          className="w-full rounded-xl border border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/50 p-3 text-sm focus:outline-none focus:border-[var(--rammy-cyan)] resize-none"
          data-testid="folder-dialog-prompt"
        />
        <div className="text-[10px] text-[var(--rammy-text-muted)] mt-1">
          Every chat inside this folder inherits this instruction — great for projects with a specific voice, subject, or role.
        </div>
        <div className="flex items-center justify-between mt-6 gap-2">
          {mode === "edit" ? (
            <button onClick={del} className="text-xs text-red-500 hover:text-red-600" data-testid="folder-dialog-delete">Delete folder</button>
          ) : <span/>}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-pill h-9 text-xs px-4 border border-[var(--rammy-border)] hover:border-[var(--rammy-text)]">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-pill btn-primary h-9 text-xs px-4" data-testid="folder-dialog-save">
              {saving ? "…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuggestDialog({ loading, suggestions, setSuggestions, onClose, onApply }) {
  const toggle = (i) => setSuggestions(prev => prev.map((g, k) => k === i ? { ...g, checked: !g.checked } : g));
  const updateName = (i, name) => setSuggestions(prev => prev.map((g, k) => k === i ? { ...g, name } : g));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="folder-suggest-dialog">
      <div className="bg-[var(--rm-surface)] rounded-3xl w-full max-w-lg p-6 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">Auto-group</div>
        <div className="font-display text-xl mb-4">Rammy&apos;s suggested folders</div>
        {loading && <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">Thinking…</div>}
        {!loading && suggestions.length === 0 && (
          <div className="text-sm text-[var(--rammy-text-sec)] py-6">
            Rammy didn&apos;t find clear themes in your chats yet. Try again after a few more conversations, or create a folder manually.
          </div>
        )}
        {!loading && suggestions.length > 0 && (
          <div className="flex-1 overflow-y-auto subtle-scroll space-y-3 pr-1">
            {suggestions.map((g, i) => (
              <label key={i} className="flex items-start gap-3 rounded-2xl border border-[var(--rammy-border)] p-3 hover:border-[var(--rammy-cyan)] cursor-pointer" data-testid={`folder-suggest-${i}`}>
                <input type="checkbox" checked={g.checked} onChange={() => toggle(i)} className="mt-1"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{g.emoji}</span>
                    <input
                      value={g.name}
                      onChange={e => updateName(i, e.target.value)}
                      className="flex-1 text-sm font-medium bg-transparent border-b border-transparent hover:border-[var(--rammy-border)] focus:border-[var(--rammy-cyan)] outline-none"
                    />
                    <span className="text-[10px] text-[var(--rammy-text-muted)]">{g.conversation_ids.length} chats</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-pill h-9 text-xs px-4 border border-[var(--rammy-border)] hover:border-[var(--rammy-text)]">Cancel</button>
          <button onClick={onApply} disabled={loading || suggestions.length === 0} className="btn-pill btn-primary h-9 text-xs px-4" data-testid="folder-suggest-apply">
            Create folders
          </button>
        </div>
      </div>
    </div>
  );
}

